import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Record as R } from 'effect';

import { erpIntegrationApi } from '@/domain/erpIntegration/api';
import { erpIntegrationKeys } from '@/domain/erpIntegration/queries/keys';
import { companyConnectedErp } from '@/domain/erpIntegration/queries/connections.select';
import type { ErpConnections } from '@/domain/erpIntegration/schemas';
import { companyKeys } from '@/domain/companies/queries/keys';
import { updateCompanyHashAction } from '@/domain/companies/companiesActions';
import { documentsKeys } from '@/domain/documents/queries/keys';
import { queryToSearch } from '@/domain/router/helpers';
import { queryOptions } from '@/services/query/queryOptions';
import { useApiMutation } from '@/services/query/useApiMutation';
import { showApiErrorToast } from '@/services/api/errorToast';
import type { EndpointQuery, EndpointResponse } from '@/services/api/endpointTypes';

export const erpConnectionsQuery = (companyId: string) =>
  queryOptions({
    queryKey: erpIntegrationKeys.connections(companyId),
    queryFn: () => erpIntegrationApi.getConnections({ path: { companyId } }),
    // Re-read only when a connection write or the sync event invalidates it, or when a
    // reader that must see the backend's own status changes asks with refetchOnMount.
    staleTime: Infinity,
  });

export const useErpConnections = <TData = ErpConnections>(
  companyId: string | null,
  { refetchOnMount, select }: { refetchOnMount?: 'always'; select?: (connections: ErpConnections) => TData } = {},
) => useQuery({ ...erpConnectionsQuery(companyId ?? ''), refetchOnMount, select, enabled: companyId !== null });

// After a write that changes the company's ERP connection: everything that depends on the connection is read again.
export const refreshAfterErpConnectionChange = async (qc: QueryClient, dispatch: Dispatch, companyId: string) => {
  // Cancels a read that started before the write, so the cards never show the old connection. Read again only where
  // a mounted reader shows them: after the user has left this company nothing is requested for it — the request would
  // carry the next company's CompanyId header, and its refusal signs the user out.
  await qc.invalidateQueries({ queryKey: erpIntegrationKeys.connections(companyId), exact: true });
  qc.invalidateQueries({ queryKey: companyKeys.capabilities(companyId), exact: true });
  qc.invalidateQueries({ queryKey: documentsKeys.exportFormats(companyId), exact: true });

  const connectionsKey = erpConnectionsQuery(companyId).queryKey;
  const connections = qc.getQueryData(connectionsKey);
  // Not read again — no reader left, or the read failed: the form and the flag keep what they show.
  if (!connections || qc.getQueryState(connectionsKey)?.isInvalidated !== false) return;

  const connectedErp = companyConnectedErp(connections);
  // A form cached for a disconnected ERP must not show up on a later connect.
  if (connectedErp) qc.invalidateQueries({ queryKey: erpIntegrationKeys.preferenceFields(companyId) });
  else qc.removeQueries({ queryKey: erpIntegrationKeys.preferenceFields(companyId) });

  // The companies list is still Redux: its tile reads this flag and is not re-read when the user goes back to it.
  dispatch({ type: updateCompanyHashAction.type, payload: { isConnectedToErp: connectedErp !== null }, companyId });
};

export const useRefreshAfterErpConnectionChange = (companyId: string) => {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  return useCallback(() => refreshAfterErpConnectionChange(qc, dispatch, companyId), [qc, dispatch, companyId]);
};

export const useActivateErpMutation = (companyId: string) => {
  const refresh = useRefreshAfterErpConnectionChange(companyId);
  return useApiMutation({
    mutationFn: (erpType: EndpointQuery<typeof erpIntegrationApi.activate>['erpType']) =>
      erpIntegrationApi.activate({ query: { erpType } }),
    onSuccess: refresh,
    onError: (error) => showApiErrorToast(error),
  });
};

export const useDisconnectErpMutation = (companyId: string) => {
  const refresh = useRefreshAfterErpConnectionChange(companyId);
  return useApiMutation({
    mutationFn: () => erpIntegrationApi.disconnect(),
    onSuccess: refresh,
    onError: (error) => showApiErrorToast(error),
  });
};

// Xero answers with a ready URL and no params; a second `?` would corrupt its last parameter.
const ssoSignInUrl = ({ endpoint, params }: EndpointResponse<typeof erpIntegrationApi.getSSOParams>) =>
  R.isEmptyRecord(params) ? endpoint : `${endpoint}?${queryToSearch(params)}`;

export const useErpSSOSignInMutation = () =>
  useApiMutation({
    mutationFn: (erpType: EndpointQuery<typeof erpIntegrationApi.getSSOParams>['erpType']) =>
      erpIntegrationApi.getSSOParams({ query: { erpType } }),
    // No refresh: the page leaves for the ERP's sign-in.
    onSuccess: (sso) => window.location.assign(ssoSignInUrl(sso)),
    onError: (error) => showApiErrorToast(error),
  });
